/* eslint-disable max-lines */
import {
  OnResumeSuccessHandler,
  OnTokenizeSuccessHandler,
} from '@primer-io/checkout-web'
import { paymentApi, userApi } from 'api'
import i18n from 'i18next'

import {
  setErrorAction,
  startFetching,
  stopFetching,
} from 'root-redux/actions/common'
import {
  getUserStatusAction,
  sendUserConfigAction,
  updateUserConfigAction,
} from 'root-redux/actions/user'
import { selectCurrentVariantCohort } from 'root-redux/selects/common'
import {
  selectPrimerProcessorName,
  selectUUID,
  selectUserPaymentId,
} from 'root-redux/selects/user'

import { captureFunnelError } from 'helpers/captureFunnelError'
import { FunnelErrorCode } from 'helpers/funnelErrors'

import {
  PRIMER_PAYMENT_ERRORS,
  PaymentMethod,
  PaymentSystem,
} from 'modules/purchase/constants'
import { getPurchaseFailedEventParams } from 'modules/purchase/helpers/getPurchaseFailedEventParams'
import { getPurchaseSuccessEventParams } from 'modules/purchase/helpers/getPurchaseSuccessEventParams'
import { logSuccessfulPayment } from 'modules/purchase/helpers/rootHelpers'
import { TPrimerTokenData } from 'modules/purchase/types'

import { IAction, IAppState, TAppDispatchThunk } from 'models/store.model'

import { eventLogger } from 'services/eventLogger.service'

import {
  selectCurrency,
  selectIsFirstPaymentRetryPassed,
  selectPaymentMethod,
  selectPlanId,
  selectPrimerClientSessionToken,
  selectSubscriptionFullPrice,
  selectSubscriptionLookupKey,
  selectSubscriptionPeriodName,
  selectSubscriptionPeriodQuantity,
  selectSubscriptionTrialPeriodDays,
  selectSubscriptionTrialPeriodPrice,
} from '../selects/common'

const MODULE_NAME = 'PAYMENT_PRIMER'

export const PURCHASE_PRIMER = `${MODULE_NAME}/PURCHASE_PRIMER`
export const PRIMER_PURCHASE_PROCESSING = `${MODULE_NAME}/PRIMER_PURCHASE_PROCESSING`
export const SET_PRIMER_CLIENT_SESSION_TOKEN = `${MODULE_NAME}/SET_PRIMER_CLIENT_SESSION_TOKEN`
export const GET_PRIMER_CLIENT_SESSION_TOKEN = `${MODULE_NAME}/GET_PRIMER_CLIENT_SESSION_TOKEN`
export const UPDATE_PRIMER_CLIENT_SESSION = `${MODULE_NAME}/UPDATE_PRIMER_CLIENT_SESSION`
export const CANCEL_PRIMER_CLIENT_SESSION_DISCOUNT = `${MODULE_NAME}/CANCEL_PRIMER_CLIENT_SESSION_DISCOUNT`
export const SET_IS_PRIMER_RETRY_PROCESSING = `${MODULE_NAME}/SET_IS_PRIMER_RETRY_PROCESSING`
export const SET_IS_FIRST_PAYMENT_RETRY_PASSED = `${MODULE_NAME}/SET_IS_FIRST_PAYMENT_RETRY_PASSED`

export const setPrimerClientSessionTokenAction = (
  payload: TPrimerTokenData,
): IAction<TPrimerTokenData> => ({
  type: SET_PRIMER_CLIENT_SESSION_TOKEN,
  payload,
})

export function setIsPrimerRetryProcessing(
  isPrimerRetryProcessing: boolean,
): IAction<boolean> {
  return {
    type: SET_IS_PRIMER_RETRY_PROCESSING,
    payload: isPrimerRetryProcessing,
  }
}

export const setIsFirstPaymentRetryPassedAction = (
  payload: boolean,
): IAction<boolean> => ({
  type: SET_IS_FIRST_PAYMENT_RETRY_PASSED,
  payload,
})

export function getPrimerClientSessionTokenAction(): any {
  return async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ) => {
    const state = getState()

    const uuid = selectUUID(state)
    const planId = selectPlanId(state)
    const trialPeriodDays = selectSubscriptionTrialPeriodDays(state)
    const cohort = selectCurrentVariantCohort(state)

    dispatch(startFetching(GET_PRIMER_CLIENT_SESSION_TOKEN))
    dispatch(setPrimerClientSessionTokenAction({ token: '', planId: null }))

    try {
      const response = await paymentApi.getPrimerClientSessionToken({
        uuid,
        cohort,
        planId,
        trialPeriodDays,
      })

      if (!response.success && response.status === 409) {
        dispatch(
          setErrorAction(
            response?.data?.message || i18n.t('commonComponents.commonError'),
          ),
        )
        await dispatch(getUserStatusAction(uuid))
        return
      }

      if (response.success && response.data) {
        const token = response.data.client_token
        const currentPlanId = selectPlanId(getState())

        if (currentPlanId === planId) {
          dispatch(setPrimerClientSessionTokenAction({ token, planId }))

          if (response.data.processor_name) {
            dispatch(
              sendUserConfigAction({
                processor_name: response.data.processor_name,
              }),
            )
            dispatch(
              updateUserConfigAction({
                processorName: response.data.processor_name,
              }),
            )
          }
        }
      }
    } catch (error) {
      captureFunnelError(FunnelErrorCode.PRIMER_SESSION_TOKEN_FAILED, error, {
        tags: { payment_system: PaymentSystem.PRIMER },
        extra: { planId, cohort },
      })
      dispatch(
        setErrorAction(error instanceof Error ? error.message : String(error)),
      )
    } finally {
      dispatch(stopFetching(GET_PRIMER_CLIENT_SESSION_TOKEN))
    }
  }
}

export function updatePrimerClientSessionAction(
  retryCallback: () => void,
): any {
  return async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ) => {
    const state = getState()
    const uuid = selectUUID(state)
    const { token: clientToken, planId } = selectPrimerClientSessionToken(state)
    const paymentId = selectUserPaymentId(state)
    const isFirstPaymentRetryPassed = selectIsFirstPaymentRetryPassed(state)

    if (isFirstPaymentRetryPassed) {
      dispatch(stopFetching(PURCHASE_PRIMER))
      dispatch(setIsPrimerRetryProcessing(false))
      return
    }

    dispatch(startFetching(UPDATE_PRIMER_CLIENT_SESSION))

    try {
      const response = await paymentApi.updatePrimerClientSession({
        uuid,
        paymentId,
        clientToken,
      })

      dispatch(stopFetching(UPDATE_PRIMER_CLIENT_SESSION))

      if (response.success && response.data && response.data.should_retry) {
        dispatch(stopFetching(PURCHASE_PRIMER))
        dispatch(
          setPrimerClientSessionTokenAction({
            token: response.data.client_token,
            planId,
          }),
        )
        dispatch(setIsPrimerRetryProcessing(true))

        retryCallback()
        return
      }

      dispatch(setIsPrimerRetryProcessing(false))
      // dispatch(setErrorAction(error))
      dispatch(stopFetching(UPDATE_PRIMER_CLIENT_SESSION))
      dispatch(stopFetching(PURCHASE_PRIMER))
    } catch (error: any) {
      captureFunnelError(FunnelErrorCode.PRIMER_SESSION_UPDATE_FAILED, error, {
        tags: { payment_system: PaymentSystem.PRIMER },
        extra: { paymentId, planId },
      })
      dispatch(setIsPrimerRetryProcessing(false))
      dispatch(setErrorAction(error.toString()))
      dispatch(stopFetching(PURCHASE_PRIMER))
      dispatch(stopFetching(UPDATE_PRIMER_CLIENT_SESSION))
    }
  }
}

export function cancelPrimerClientSessionDiscount({
  clientToken,
}: {
  clientToken: string
}): any {
  return async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ) => {
    const state = getState()
    const uuid = selectUUID(state)

    dispatch(startFetching(CANCEL_PRIMER_CLIENT_SESSION_DISCOUNT))

    try {
      const response = await paymentApi.updatePrimerClientSession({
        uuid,
        clientToken,
      })

      dispatch(stopFetching(CANCEL_PRIMER_CLIENT_SESSION_DISCOUNT))

      if (!response.success && response?.data?.error) {
        dispatch(setErrorAction(i18n.t(PRIMER_PAYMENT_ERRORS.COMMON_ERROR)))
        return
      }
    } catch (error: any) {
      console.error('[cancelPrimerClientSessionDiscount] Error:', error)
      dispatch(stopFetching(CANCEL_PRIMER_CLIENT_SESSION_DISCOUNT))
      dispatch(setErrorAction(error.toString()))
      dispatch(stopFetching(PURCHASE_PRIMER))
    }
  }
}

export function primerConfirmPaymentAction({
  paymentId,
  handler,
}: {
  paymentId: string
  handler: OnTokenizeSuccessHandler
}): any {
  return async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ) => {
    const state = getState()
    const uuid = selectUUID(state)
    const paymentMethod = selectPaymentMethod(state)
    const processorName = selectPrimerProcessorName(state)

    const commonPurchaseFailedParams = getPurchaseFailedEventParams(state)

    try {
      const response = await paymentApi.confirmPrimerSubscription({
        uuid,
        paymentId,
      })

      if (!response.data && !window.navigator.onLine) {
        dispatch(setErrorAction(i18n.t(PRIMER_PAYMENT_ERRORS.COMMON_ERROR)))
        dispatch(stopFetching(PURCHASE_PRIMER))
        dispatch(setIsPrimerRetryProcessing(false))
        return
      }

      if (!response.success && response?.data?.error) {
        eventLogger.logPurchaseFailed({
          ...commonPurchaseFailedParams,
          paymentMethod,
          error: response?.data?.error,
          paymentSystem: PaymentSystem.PRIMER,
          processorName,
        })
        dispatch(setErrorAction(response?.data?.error))
        dispatch(stopFetching(PURCHASE_PRIMER))
        dispatch(setIsPrimerRetryProcessing(false))

        handler.handleFailure(response?.data?.error)
        return
      }

      dispatch(stopFetching(PURCHASE_PRIMER))
      dispatch(setIsPrimerRetryProcessing(false))
      handler.handleSuccess()
    } catch (error: any) {
      dispatch(setErrorAction(error.toString()))
      dispatch(setIsPrimerRetryProcessing(false))
      dispatch(stopFetching(PURCHASE_PRIMER))
      handler.handleFailure(error.toString())
    }
  }
}

export function primerPurchaseAction({
  token,
  handler,
  retryPaymentCallback,
  upsellPages,
}: {
  token: string
  handler: OnTokenizeSuccessHandler
  retryPaymentCallback: () => void
  upsellPages: string[]
}): any {
  return async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ) => {
    const state = getState()
    const uuid = selectUUID(state)
    const paymentMethod = selectPaymentMethod(state)
    const priceId = selectSubscriptionLookupKey(state)
    const planId = selectPlanId(state)
    const trialPrice = selectSubscriptionTrialPeriodPrice(state)
    const trialPeriodDays = selectSubscriptionTrialPeriodDays(state)
    const currentPrice = selectSubscriptionFullPrice(state)
    const discountTried = selectIsFirstPaymentRetryPassed(state)
    const periodName = selectSubscriptionPeriodName(state)
    const periodQuantity = selectSubscriptionPeriodQuantity(state)
    const currency = selectCurrency(state)
    const processorName = selectPrimerProcessorName(state)

    const commonPurchaseFailedParams = getPurchaseFailedEventParams(state)
    const commonPurchaseSuccessParams = getPurchaseSuccessEventParams(state)

    dispatch(startFetching(PURCHASE_PRIMER))
    dispatch(
      sendUserConfigAction({
        payment_system: PaymentSystem.PRIMER,
        payment_method: paymentMethod,
        trial_period: trialPeriodDays,
        payment_currency: currency,
        subscription_price: currentPrice,
        subscription_duration: `${periodQuantity}${periodName}`,
        price_id: priceId,
        trial_price: trialPrice,
        unvisited_upsell_pages: upsellPages,
      }),
    )

    try {
      const response = await paymentApi.createPrimerSubscription({
        uuid,
        token,
      })

      if (!response.data && !window.navigator.onLine) {
        dispatch(setErrorAction(i18n.t(PRIMER_PAYMENT_ERRORS.COMMON_ERROR)))
        dispatch(stopFetching(PURCHASE_PRIMER))
        dispatch(setIsPrimerRetryProcessing(false))
        return
      }

      if (!response.data || !response.data.payment) {
        console.error(
          '[Primer action] Invalid response structure:',
          response.data,
        )
        dispatch(setErrorAction(i18n.t(PRIMER_PAYMENT_ERRORS.COMMON_ERROR)))
        dispatch(stopFetching(PURCHASE_PRIMER))
        dispatch(setIsPrimerRetryProcessing(false))
        return
      }

      if (!response.data.payment.is_success) {
        const error = {
          type: response.data.payment.status_reason.type,
          description: response.data.payment.status_reason.message,
          code: response.data.payment.status_reason.code,
        }

        eventLogger.logPurchaseFailed({
          ...commonPurchaseFailedParams,
          paymentMethod,
          error,
          paymentSystem: PaymentSystem.PRIMER,
          processorName,
        })

        dispatch(
          setErrorAction(
            i18n.t(
              PRIMER_PAYMENT_ERRORS[error.code?.toUpperCase()] ||
                PRIMER_PAYMENT_ERRORS.COMMON_ERROR,
            ),
          ),
        )
        dispatch(stopFetching(PURCHASE_PRIMER))
        dispatch(setIsPrimerRetryProcessing(false))

        handler.handleFailure(error.description)

        if (paymentMethod === PaymentMethod.CREDIT_CARD && !discountTried) {
          dispatch(
            updateUserConfigAction({
              paymentId: response.data.payment.payment_id,
            }),
          )
          dispatch(updatePrimerClientSessionAction(retryPaymentCallback))
        }

        return
      }

      if (response.data.payment.client_token) {
        dispatch(
          setPrimerClientSessionTokenAction({
            token: response.data.payment.client_token,
            planId,
          }),
        )

        sendUserConfigAction({
          payment_system: PaymentSystem.PRIMER,
          payment_currency: currency,
          payment_method: paymentMethod,
          subscription_price: currentPrice,
          subscription_duration: `${periodQuantity}${periodName}`,
          price_id: priceId,
          trial_price: trialPrice,
          trial_period: trialPeriodDays,
          payment_id: response.data.payment.payment_id,
          unvisited_upsell_pages: upsellPages,
        })

        dispatch(
          updateUserConfigAction({
            paymentId: response.data.payment.payment_id,
          }),
        )

        handler.continueWithNewClientToken(response.data.payment.client_token)
        return
      }

      dispatch(
        primerConfirmPaymentAction({
          paymentId: response.data.payment.payment_id,
          handler,
        }),
      )

      const predictedLtvResponse = await userApi.getPredictedLtv(uuid)

      logSuccessfulPayment({
        ...commonPurchaseSuccessParams,
        productId: response.data.purchase.product_id,
        price: currentPrice,
        trialPrice,
        trialPeriodDays,
        predictedLtv: predictedLtvResponse?.data?.predicted_ltv,
        discountApplied: response.data.purchase.discount_applied,
        subscriptionId: response.data.purchase.subscription_id,
        paymentMethod,
        paymentSystem: PaymentSystem.PRIMER,
      })

      dispatch(
        sendUserConfigAction({
          payment_system: PaymentSystem.PRIMER,
          payment_currency: currency,
          payment_method: paymentMethod,
          subscription_price: currentPrice,
          subscription_duration: `${periodQuantity}${periodName}`,
          price_id: priceId,
          trial_price: trialPrice,
          trial_period: trialPeriodDays,
          unvisited_upsell_pages: upsellPages,
        }),
      )
    } catch (error: any) {
      dispatch(setErrorAction(error.toString()))
      dispatch(setIsPrimerRetryProcessing(false))
      dispatch(stopFetching(PURCHASE_PRIMER))
      handler.handleFailure(error.toString())
    }
  }
}

export function primerResumePurchaseAction({
  currentPaymentId,
  resumeToken,
  handler,
  retryPaymentCallback,
  upsellPages,
}: {
  currentPaymentId: string | undefined
  resumeToken: string
  handler: OnResumeSuccessHandler
  retryPaymentCallback: () => void
  upsellPages: string[]
}): any {
  return async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ) => {
    const state = getState()
    const uuid = selectUUID(state)
    const paymentMethod = selectPaymentMethod(state)
    const priceId = selectSubscriptionLookupKey(state)
    const planId = selectPlanId(state)
    const paymentId = selectUserPaymentId(state)
    const trialPeriodDays = selectSubscriptionTrialPeriodDays(state)
    const currentPrice = selectSubscriptionFullPrice(state)
    const trialPrice = selectSubscriptionTrialPeriodPrice(state)
    const discountTried = selectIsFirstPaymentRetryPassed(state)
    const periodName = selectSubscriptionPeriodName(state)
    const periodQuantity = selectSubscriptionPeriodQuantity(state)
    const currency = selectCurrency(state)
    const processorName = selectPrimerProcessorName(state)

    const commonPurchaseSuccessParams = getPurchaseSuccessEventParams(state)
    const commonPurchaseFailedParams = getPurchaseFailedEventParams(state)

    dispatch(startFetching(PURCHASE_PRIMER))

    try {
      const response = await paymentApi.resumePrimerSubscription({
        uuid,
        paymentId: currentPaymentId || paymentId,
        resumeToken,
      })

      if (!response.data && !window.navigator.onLine) {
        dispatch(setErrorAction(i18n.t(PRIMER_PAYMENT_ERRORS.COMMON_ERROR)))
        dispatch(stopFetching(PURCHASE_PRIMER))
        dispatch(setIsPrimerRetryProcessing(false))
        return
      }

      if (!response.data || !response.data.payment) {
        console.error(
          '[Primer action] Invalid response structure:',
          response.data,
        )
        dispatch(setErrorAction(i18n.t(PRIMER_PAYMENT_ERRORS.COMMON_ERROR)))
        dispatch(stopFetching(PURCHASE_PRIMER))
        dispatch(setIsPrimerRetryProcessing(false))
        return
      }

      if (!response.data.payment.is_success) {
        const error = {
          type: response.data.payment.status_reason.type,
          description: response.data.payment.status_reason.message,
          code: response.data.payment.status_reason.code,
        }

        eventLogger.logPurchaseFailed({
          ...commonPurchaseFailedParams,
          paymentMethod,
          error,
          paymentSystem: PaymentSystem.PRIMER,
          processorName,
        })

        dispatch(
          setErrorAction(
            i18n.t(
              PRIMER_PAYMENT_ERRORS[error.code?.toUpperCase()] ||
                PRIMER_PAYMENT_ERRORS.COMMON_ERROR,
            ),
          ),
        )
        dispatch(stopFetching(PURCHASE_PRIMER))
        dispatch(setIsPrimerRetryProcessing(false))

        handler.handleFailure(error.description)

        if (paymentMethod === PaymentMethod.CREDIT_CARD && !discountTried) {
          dispatch(
            updateUserConfigAction({
              paymentId: response.data.payment.payment_id,
            }),
          )

          dispatch(updatePrimerClientSessionAction(retryPaymentCallback))
        }

        return
      }

      if (response.data.payment.client_token) {
        dispatch(
          setPrimerClientSessionTokenAction({
            token: response.data.payment.client_token,
            planId,
          }),
        )

        handler.continueWithNewClientToken(response.data.payment.client_token)
        return
      }

      dispatch(
        primerConfirmPaymentAction({
          paymentId: response.data.payment.payment_id,
          handler,
        }),
      )

      const predictedLtvResponse = await userApi.getPredictedLtv(uuid)

      logSuccessfulPayment({
        ...commonPurchaseSuccessParams,
        productId: response.data.purchase.product_id,
        price: currentPrice,
        trialPrice,
        trialPeriodDays,
        predictedLtv: predictedLtvResponse?.data?.predicted_ltv,
        discountApplied: response.data.purchase.discount_applied,
        subscriptionId: response.data.purchase.subscription_id,
        paymentMethod,
        paymentSystem: PaymentSystem.PRIMER,
        processorName,
      })

      dispatch(
        sendUserConfigAction({
          payment_system: PaymentSystem.PRIMER,
          payment_currency: currency,
          payment_method: paymentMethod,
          subscription_price: currentPrice,
          subscription_duration: `${periodQuantity}${periodName}`,
          price_id: priceId,
          trial_price: trialPrice,
          trial_period: trialPeriodDays,
          unvisited_upsell_pages: upsellPages,
        }),
      )
      handler.handleSuccess()
    } catch (error: any) {
      dispatch(setErrorAction(error.toString()))
      dispatch(setIsPrimerRetryProcessing(false))
      dispatch(stopFetching(PURCHASE_PRIMER))
      handler.handleFailure(error.toString())
    } finally {
      dispatch(stopFetching(PRIMER_PURCHASE_PROCESSING))
    }
  }
}
